import React, { useState, useMemo, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, ShoppingBag, ClipboardList, CheckCircle, Truck, Inbox, CalendarClock, Package, Pencil } from 'lucide-react';
import { productionAPI } from '../../api';

// Helpers
const todayLocalISO = () => new Date().toLocaleDateString('en-CA');
const dateOnly = (iso) => (iso ? String(iso).slice(0, 10) : '');
const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const totalQty = (o) => (o.items || []).reduce((s, it) => s + Number(it.quantity || 0), 0);
const tomorrowISO = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toLocaleDateString('en-CA');
};

// Tile configuration. Each tile knows how to filter the dashboard count + how to drill into Orders.
// Distinct vibrant color per tile so they're visually scannable at a glance.
const TILES = [
  { id: 'pending', label: 'Pending', icon: ClipboardList, color: '#E89D4F', accent: '#FFFFFF' },           // amber
  { id: 'confirmed', label: 'Confirmed', icon: CheckCircle, color: '#2B5339', accent: '#FFFFFF' },         // forest green
  { id: 'dispatched', label: 'Dispatched', icon: Truck, color: '#C05C35', accent: '#FFFFFF' },             // rust
  { id: 'received', label: 'Orders Received', icon: Inbox, color: '#3F6B8A', accent: '#FFFFFF' },          // slate blue
  { id: 'all', label: 'All Orders', icon: ShoppingBag, color: '#7B5FA0', accent: '#FFFFFF' },              // muted purple
];

const DISPATCHED_SET = new Set(['dispatched', 'partial_dispatched', 'delivered']);

// Compute the order list represented by a tile (used for the tile's summary numbers)
const computeForTile = (orders, tileId, today) => {
  if (tileId === 'pending') return orders.filter((o) => o.status === 'pending' && dateOnly(o.delivery_date) === today);
  if (tileId === 'confirmed') return orders.filter((o) => o.status === 'confirmed' && dateOnly(o.delivery_date) === today);
  if (tileId === 'dispatched') return orders.filter((o) => DISPATCHED_SET.has(o.status) && dateOnly(o.delivery_date) === today);
  if (tileId === 'received') return orders.filter((o) => (o.status === 'pending' || o.status === 'confirmed') && dateOnly(o.created_at) === today && dateOnly(o.delivery_date) > today);
  if (tileId === 'all') return orders.filter((o) => (o.status === 'pending' || o.status === 'confirmed'));
  return [];
};

// Build the drillFilter payload to navigate into the Orders tab with the right filters pre-applied
const buildDrillFilter = (tileId, today) => {
  const tomorrow = tomorrowISO();
  if (tileId === 'pending') {
    return { type: 'status', value: 'pending', status_filter: 'pending', sched_from: today, sched_to: today };
  }
  if (tileId === 'confirmed') {
    return { type: 'status', value: 'confirmed', status_filter: 'confirmed', sched_from: today, sched_to: today };
  }
  if (tileId === 'dispatched') {
    return { type: 'status', value: 'dispatched', status_filter: 'dispatched', sched_from: today, sched_to: today, include_dispatched: true };
  }
  if (tileId === 'received') {
    return { type: 'status', value: 'all', status_filter: 'all', date_from: today, date_to: today, sched_from: tomorrow, sched_to: '' };
  }
  // all → no schedule date filter, sorted by order date ascending
  return { type: 'status', value: 'all', status_filter: 'all', date_from: '', date_to: '', sched_from: '', sched_to: '', sort_column: 'order_date', sort_order: 'oldest' };
};

export const DashboardTab = ({ orders = [], onNewOrder, onDrillIn, onEditOrder }) => {
  const today = todayLocalISO();
  const [expandedHeatmapDay, setExpandedHeatmapDay] = useState(null);
  // Product row expand-state on the heatmap breakdown table. Only one row can
  // be expanded at a time. Cleared when the heatmap day changes.
  const [expandedProduct, setExpandedProduct] = useState(null);
  // Drill-orders dialog when a dealer row is clicked. Mirrors the Planning
  // tab's dealer→order flow: list matching orders, offer Edit Order for
  // pending/confirmed ones. Cleared on close.
  const [drillOrders, setDrillOrders] = useState(null);
  useEffect(() => { setExpandedProduct(null); }, [expandedHeatmapDay]);
  // Planning-style rows (Demand, E-302, E-303, Total, Shortfall, Excess) for the
  // currently-expanded heatmap day. Fetched from productionAPI.getPlan so the
  // numbers match the Planning tab exactly.
  const [heatmapPlan, setHeatmapPlan] = useState({ date: null, rows: [], loading: false });
  // Sort config for the heatmap breakdown table. Currently supports Shortfall
  // and Excess columns; when null the table falls back to the default sort
  // (Demand remaining, descending) so behavior is unchanged until a header
  // is clicked. dir cycles: null → desc → asc → null.
  const [heatmapSort, setHeatmapSort] = useState({ key: null, dir: null });
  const cycleHeatmapSort = (key) => {
    setHeatmapSort((prev) => {
      if (prev.key !== key) return { key, dir: 'desc' };
      if (prev.dir === 'desc') return { key, dir: 'asc' };
      return { key: null, dir: null };
    });
  };
  useEffect(() => { setHeatmapSort({ key: null, dir: null }); }, [expandedHeatmapDay]);

  // Re-fetch the planning breakdown whenever the expanded day changes. Dropped
  // (via seq guard) if the operator rapidly toggles between days.
  const planSeqRef = React.useRef(0);
  useEffect(() => {
    if (!expandedHeatmapDay) { setHeatmapPlan({ date: null, rows: [], loading: false }); return; }
    const seq = ++planSeqRef.current;
    setHeatmapPlan({ date: expandedHeatmapDay, rows: [], loading: true });
    productionAPI.getPlan({ date_from: expandedHeatmapDay, date_to: expandedHeatmapDay, group_by_parent: false })
      .then((res) => {
        if (seq !== planSeqRef.current) return;
        setHeatmapPlan({ date: expandedHeatmapDay, rows: res.data?.rows || [], loading: false });
      })
      .catch(() => {
        if (seq !== planSeqRef.current) return;
        setHeatmapPlan({ date: expandedHeatmapDay, rows: [], loading: false });
      });
  }, [expandedHeatmapDay]);

  // Per-tile summaries (count, bags, amount) — always reflect today
  const tileSummaries = useMemo(() => {
    return Object.fromEntries(TILES.map((t) => {
      const rows = computeForTile(orders, t.id, today);
      return [t.id, {
        count: rows.length,
        bags: rows.reduce((s, o) => s + totalQty(o), 0),
        amount: rows.reduce((s, o) => s + Number(o.total_amount || 0), 0),
      }];
    }));
  }, [orders, today]);

  // 4-day heatmap: today + next 3 days. Pending+Confirmed bags scheduled per day.
  const heatmapDays = useMemo(() => {
    const out = [];
    const base = new Date(today + 'T00:00:00');
    for (let i = 0; i < 4; i++) {
      const d = new Date(base);
      d.setDate(d.getDate() + i);
      out.push(d.toLocaleDateString('en-CA'));
    }
    const acc = Object.fromEntries(out.map((d) => [d, { bags: 0, orders: 0, byProduct: {} }]));
    for (const o of orders) {
      if (o.status !== 'pending' && o.status !== 'confirmed') continue;
      const d = dateOnly(o.delivery_date);
      if (!acc[d]) continue;
      acc[d].bags += totalQty(o);
      acc[d].orders += 1;
      for (const it of (o.items || [])) {
        const pid = it.product_id || it.product_name;
        const cell = acc[d].byProduct[pid] = acc[d].byProduct[pid] || { product_name: it.product_name, bags: 0, orders: 0 };
        cell.bags += Number(it.quantity || 0);
        cell.orders += 1;
      }
    }
    const max = Math.max(1, ...out.map((d) => acc[d].bags));
    return out.map((d) => ({ date: d, ...acc[d], pct: acc[d].bags / max }));
  }, [orders, today]);

  const handleTileClick = (tileId) => {
    if (onDrillIn) onDrillIn(buildDrillFilter(tileId, today), tileId);
  };

  return (
    <div data-testid="admin-dashboard-section">
      {/* ~3 lines of breathing room before the New Order button */}
      <div className="h-16 md:h-20" aria-hidden="true" />

      <div className="mb-6">
        <button
          onClick={onNewOrder}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-lg font-semibold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
          style={{ background: '#C05C35' }}
          data-testid="dashboard-new-order-btn"
        >
          <Plus className="w-5 h-5" /> New Order
        </button>
      </div>

      {/* Order Status — tiles only (clicking a tile drills into the Orders tab) */}
      <Card className="mb-6 bg-white border" style={{ borderColor: '#E2DFD8' }}>
        <div className="px-4 md:px-5 py-3 border-b flex items-center gap-2" style={{ borderColor: '#E2DFD8', background: '#FBF9F6' }}>
          <ShoppingBag className="w-5 h-5" style={{ color: '#2B5339' }} />
          <h3 className="text-lg md:text-xl font-bold" style={{ color: '#2B5339' }}>Order Status</h3>
          <span className="text-[11px] ml-2" style={{ color: '#5C6B61' }}>Click a tile to open the filtered orders</span>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 p-2.5 md:p-3" data-testid="dashboard-order-tiles">
          {TILES.map((t) => {
            const s = tileSummaries[t.id] || { count: 0, bags: 0, amount: 0 };
            return (
              <button
                key={t.id}
                onClick={() => handleTileClick(t.id)}
                className="text-left p-2.5 rounded-md border transition-all hover:-translate-y-0.5 hover:shadow-md"
                style={{ background: t.color, borderColor: t.color, color: t.accent }}
                data-testid={`dashboard-tab-${t.id}`}
                title={`Open ${t.label}`}
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <t.icon className="w-3.5 h-3.5" style={{ color: t.accent }} />
                  <p className="text-[11px] md:text-xs font-semibold truncate" style={{ color: t.accent }}>{t.label}</p>
                </div>
                <p className="text-xl md:text-2xl font-extrabold leading-none" style={{ color: t.accent }} data-testid={`dashboard-tile-count-${t.id}`}>
                  {s.count}
                </p>
                <p className="text-[9px] mt-0.5 uppercase tracking-wide" style={{ color: 'rgba(255,255,255,0.85)' }}>orders</p>
                <div className="mt-1.5 pt-1.5 border-t flex items-baseline justify-between gap-1" style={{ borderColor: 'rgba(255,255,255,0.25)' }}>
                  <span className="text-xs font-bold" style={{ color: t.accent }}>{s.bags.toLocaleString('en-IN')}</span>
                  <span className="text-[9px]" style={{ color: 'rgba(255,255,255,0.85)' }}>bags</span>
                </div>
                <div className="text-[10px] font-semibold mt-0.5 truncate" style={{ color: t.accent }}>{fmtINR(s.amount)}</div>
                <div className="text-[9px] mt-1 font-medium" style={{ color: 'rgba(255,255,255,0.9)' }}>Open →</div>
              </button>
            );
          })}
        </div>
      </Card>

      {/* 4-day Schedule Heatmap (footer) — click a day to expand product-wise breakdown */}
      <Card className="bg-white border" style={{ borderColor: '#E2DFD8' }} data-testid="schedule-heatmap">
        <div className="px-4 md:px-5 py-2.5 border-b flex items-center gap-2 flex-wrap" style={{ borderColor: '#E2DFD8', background: '#FBF9F6' }}>
          <CalendarClock className="w-4 h-4" style={{ color: '#2B5339' }} />
          <h4 className="text-sm md:text-base font-bold" style={{ color: '#2B5339' }}>4-day Schedule Heatmap</h4>
          <span className="text-[11px] ml-2" style={{ color: '#5C6B61' }}>Pending + Confirmed bags scheduled per day · click a day to see product-wise breakdown</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 p-2.5 md:p-3" data-testid="heatmap-grid">
          {heatmapDays.map((d) => {
            const dt = new Date(d.date + 'T00:00:00');
            const isToday = d.date === today;
            const isExpanded = expandedHeatmapDay === d.date;
            const alpha = d.bags === 0 ? 0 : 0.15 + d.pct * 0.7;
            return (
              <button
                key={d.date}
                onClick={() => setExpandedHeatmapDay(isExpanded ? null : d.date)}
                className="rounded-md text-left p-2 transition-all hover:-translate-y-0.5 hover:shadow-sm border"
                style={{
                  background: d.bags === 0 ? '#FBF9F6' : `rgba(43, 83, 57, ${alpha})`,
                  borderColor: isExpanded ? '#C05C35' : (isToday ? '#2B5339' : '#E2DFD8'),
                  borderWidth: isExpanded || isToday ? 2 : 1,
                }}
                data-testid={`heatmap-day-${d.date}`}
                title={`${d.date} · ${d.orders} orders · ${d.bags} bags`}
              >
                <div className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: alpha > 0.45 ? 'white' : '#5C6B61' }}>
                  {dt.toLocaleDateString('en-IN', { weekday: 'short' })} {isToday && <span className="ml-1 px-1 rounded text-[8px]" style={{ background: '#C05C35', color: 'white' }}>TODAY</span>}
                </div>
                <div className="text-sm md:text-base font-bold" style={{ color: alpha > 0.45 ? 'white' : '#1E2420' }}>
                  {dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                </div>
                <div className="text-lg md:text-xl font-extrabold leading-tight mt-1" style={{ color: alpha > 0.45 ? 'white' : (d.bags > 0 ? '#2B5339' : '#B9BEB6') }} data-testid={`heatmap-bags-${d.date}`}>
                  {d.bags.toLocaleString('en-IN')}
                </div>
                <div className="text-[10px]" style={{ color: alpha > 0.45 ? 'rgba(255,255,255,0.85)' : '#5C6B61' }}>
                  bags · {d.orders} ord
                </div>
                <div className="mt-1 text-[10px] font-semibold" style={{ color: alpha > 0.45 ? 'rgba(255,255,255,0.85)' : '#5C6B61' }}>
                  {isExpanded ? '\u25B2 hide' : '\u25BC details'}
                </div>
              </button>
            );
          })}
        </div>

        {/* Expanded planning-style breakdown for the selected day.
            Columns mirror the Planning tab (Demand rem. · Stock E-302 · Stock
            E-303 · Total stock · Shortfall · Excess) so the operator sees the
            same math they'd use to decide production for that date. Rows come
            from productionAPI.getPlan (source of truth). */}
        {expandedHeatmapDay && (() => {
          const day = heatmapDays.find((x) => x.date === expandedHeatmapDay);
          if (!day) return null;
          const dt = new Date(expandedHeatmapDay + 'T00:00:00');
          const rows = (heatmapPlan.rows || []).filter(r => (r.ordered_remaining || 0) > 0)
            .sort((a, b) => {
              if (heatmapSort.key && heatmapSort.dir) {
                const keyMap = { shortfall: 'net_required', excess: 'excess_stock' };
                const field = keyMap[heatmapSort.key];
                const av = a[field] || 0;
                const bv = b[field] || 0;
                if (av !== bv) return heatmapSort.dir === 'asc' ? av - bv : bv - av;
              }
              return (b.ordered_remaining || 0) - (a.ordered_remaining || 0);
            });
          const totalDemand = rows.reduce((s, r) => s + (r.ordered_remaining || 0), 0);
          const totalE302 = rows.reduce((s, r) => s + ((r.stock_by_facility && r.stock_by_facility['E-302']) || 0), 0);
          const totalE303 = rows.reduce((s, r) => s + ((r.stock_by_facility && r.stock_by_facility['E-303']) || 0), 0);
          const totalStock = rows.reduce((s, r) => s + (r.total_stock || 0), 0);
          const totalShort = rows.reduce((s, r) => s + (r.net_required || 0), 0);
          const totalExcess = rows.reduce((s, r) => s + (r.excess_stock || 0), 0);
          const fmt = (n) => (Number(n) || 0).toLocaleString('en-IN');
          return (
            <div className="border-t p-3 md:p-4" style={{ borderColor: '#E2DFD8', background: '#FBF9F6' }} data-testid={`heatmap-detail-${expandedHeatmapDay}`}>
              <div className="flex items-center gap-2 mb-2.5">
                <Package className="w-4 h-4" style={{ color: '#2B5339' }} />
                <h5 className="font-bold text-sm" style={{ color: '#2B5339' }}>
                  Planning breakdown — {dt.toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' })}
                </h5>
                <span className="text-[11px] ml-auto" style={{ color: '#5C6B61' }}>
                  {day.orders} orders · {day.bags.toLocaleString('en-IN')} bags scheduled
                </span>
              </div>
              {heatmapPlan.loading ? (
                <div className="text-xs py-3 text-center" style={{ color: '#5C6B61' }}>Loading planning data…</div>
              ) : rows.length === 0 ? (
                <div className="text-xs py-3 text-center" style={{ color: '#B9BEB6' }}>No pending or confirmed demand for this day.</div>
              ) : (
                <div className="overflow-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr style={{ background: 'white' }}>
                        <th className="p-2 text-left">Product</th>
                        <th className="p-2 text-right">Demand (rem.)</th>
                        <th className="p-2 text-right">Stock E-302</th>
                        <th className="p-2 text-right">Stock E-303</th>
                        <th className="p-2 text-right">Total stock</th>
                        <th
                          className="p-2 text-right cursor-pointer select-none hover:bg-[#FBF9F6]"
                          style={{ color: '#C05C35' }}
                          onClick={() => cycleHeatmapSort('shortfall')}
                          data-testid="heatmap-sort-shortfall"
                          title="Click to sort by Shortfall (desc → asc → default)"
                        >
                          Shortfall{heatmapSort.key === 'shortfall' ? (heatmapSort.dir === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
                        </th>
                        <th
                          className="p-2 text-right cursor-pointer select-none hover:bg-[#FBF9F6]"
                          style={{ color: '#2B5339' }}
                          onClick={() => cycleHeatmapSort('excess')}
                          data-testid="heatmap-sort-excess"
                          title="Click to sort by Excess (desc → asc → default)"
                        >
                          Excess{heatmapSort.key === 'excess' ? (heatmapSort.dir === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => {
                        const e302 = (r.stock_by_facility && r.stock_by_facility['E-302']) || 0;
                        const e303 = (r.stock_by_facility && r.stock_by_facility['E-303']) || 0;
                        const shortfall = r.net_required || 0;
                        const excess = r.excess_stock || 0;
                        const dealers = r.direct_dealers || r.dealers || [];
                        const isOpen = expandedProduct === (r.product_id || r.product_name);
                        return (
                          <React.Fragment key={r.product_id || r.product_name}>
                          <tr
                            className="border-t hover:bg-white transition-colors"
                            style={{ borderColor: '#E2DFD8', cursor: dealers.length > 0 ? 'pointer' : 'default', borderBottom: isOpen ? 'none' : undefined }}
                            onClick={() => { if (dealers.length > 0) setExpandedProduct(isOpen ? null : (r.product_id || r.product_name)); }}
                            data-testid={`heatmap-plan-row-${r.product_id || r.product_name}`}
                          >
                            <td className="p-2 font-medium">
                              <div className="flex items-center gap-2 flex-wrap">
                                {dealers.length > 0 && (
                                  <span className="transition-transform" style={{ transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)', display: 'inline-block', fontSize: '10px', color: '#5C6B61' }}>&#9654;</span>
                                )}
                                {r.product_name}
                                {dealers.length > 0 && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: 'white', color: '#5C6B61', border: '1px solid #E2DFD8' }}>
                                    {dealers.length} dealer{dealers.length !== 1 ? 's' : ''}
                                  </span>
                                )}
                              </div>
                              <div className="text-[10px]" style={{ color: '#5C6B61' }}>{r.order_count || 0} order{(r.order_count || 0) === 1 ? '' : 's'}</div>
                            </td>
                            <td className="p-2 text-right font-bold tabular-nums" style={{ color: '#1F1F1F' }}>{fmt(r.ordered_remaining)}</td>
                            <td className="p-2 text-right tabular-nums" style={{ color: e302 === 0 ? '#B9BEB6' : '#1F1F1F' }}>{fmt(e302)}</td>
                            <td className="p-2 text-right tabular-nums" style={{ color: e303 === 0 ? '#B9BEB6' : '#1F1F1F' }}>{fmt(e303)}</td>
                            <td className="p-2 text-right font-bold tabular-nums" style={{ color: '#1F1F1F' }}>{fmt(r.total_stock)}</td>
                            <td className="p-2 text-right tabular-nums" style={{ color: shortfall > 0 ? '#C05C35' : '#B9BEB6', fontWeight: shortfall > 0 ? 700 : 400 }}>{shortfall > 0 ? fmt(shortfall) : '-'}</td>
                            <td className="p-2 text-right tabular-nums" style={{ color: excess > 0 ? '#2B5339' : '#B9BEB6', fontWeight: excess > 0 ? 700 : 400 }}>{excess > 0 ? fmt(excess) : '-'}</td>
                          </tr>
                          {isOpen && dealers.length > 0 && (
                            <tr style={{ background: '#F6F1EA', borderBottom: '1px solid #E2DFD8' }} data-testid={`heatmap-dealer-panel-${r.product_id || r.product_name}`}>
                              <td colSpan={7} className="p-0">
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr style={{ background: '#FBF9F6' }}>
                                      <th className="text-left p-2 pl-8 font-semibold" style={{ color: '#5C6B61' }}>Dealer (click to view orders)</th>
                                      <th className="text-right p-2 font-semibold" style={{ color: '#5C6B61' }}>Demand (rem.)</th>
                                      <th className="text-right p-2 pr-4 font-semibold" style={{ color: '#5C6B61' }}># Orders</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {dealers.map((de) => {
                                      const openDealer = () => {
                                        const ids = new Set(de.order_ids || []);
                                        const matches = (orders || [])
                                          .filter(o => ids.has(o.id) && o.dealer_id === de.dealer_id)
                                          .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
                                        if (matches.length === 0) return;
                                        setDrillOrders({ orders: matches, product_id: r.product_id, product_name: r.product_name, dealer_name: de.dealer_name });
                                      };
                                      return (
                                        <tr key={de.dealer_id} className="border-t hover:bg-white transition-colors" style={{ borderColor: '#E2DFD8', cursor: 'pointer' }} onClick={openDealer} data-testid="heatmap-dealer-row">
                                          <td className="p-2 pl-8 font-medium">{de.dealer_name}</td>
                                          <td className="p-2 text-right font-medium tabular-nums" style={{ color: '#2B5339' }}>{fmt(de.quantity)}</td>
                                          <td className="p-2 pr-4 text-right tabular-nums" style={{ color: '#5C6B61' }}>{de.order_count || 0}</td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          )}
                          </React.Fragment>
                        );
                      })}
                      {/* Grand-totals row */}
                      <tr className="border-t font-bold" style={{ borderColor: '#2B5339', background: 'white' }} data-testid="heatmap-plan-totals">
                        <td className="p-2" style={{ color: '#2B5339' }}>TOTAL</td>
                        <td className="p-2 text-right tabular-nums" style={{ color: '#2B5339' }}>{fmt(totalDemand)}</td>
                        <td className="p-2 text-right tabular-nums" style={{ color: '#2B5339' }}>{fmt(totalE302)}</td>
                        <td className="p-2 text-right tabular-nums" style={{ color: '#2B5339' }}>{fmt(totalE303)}</td>
                        <td className="p-2 text-right tabular-nums" style={{ color: '#2B5339' }}>{fmt(totalStock)}</td>
                        <td className="p-2 text-right tabular-nums" style={{ color: totalShort > 0 ? '#C05C35' : '#B9BEB6' }}>{totalShort > 0 ? fmt(totalShort) : '-'}</td>
                        <td className="p-2 text-right tabular-nums" style={{ color: totalExcess > 0 ? '#2B5339' : '#B9BEB6' }}>{totalExcess > 0 ? fmt(totalExcess) : '-'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })()}
      </Card>

      {/* Dealer→orders drill dialog (mirrors Planning tab). Each order card is
          collapsed to the essentials — number, date, items with qty, total —
          and includes an Edit Order button that jumps to the Orders tab with
          the edit dialog auto-opened. */}
      <Dialog open={!!drillOrders} onOpenChange={(o) => { if (!o) setDrillOrders(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ color: '#2B5339' }}>
              {drillOrders?.dealer_name} — {drillOrders?.product_name}
            </DialogTitle>
          </DialogHeader>
          {drillOrders && drillOrders.orders.length === 0 ? (
            <div className="text-center py-6 text-xs" style={{ color: '#5C6B61' }}>No orders found.</div>
          ) : (
            <div className="space-y-3">
              {(drillOrders?.orders || []).map((o) => {
                const items = (o.items || []).filter(it => !drillOrders?.product_id || it.product_id === drillOrders.product_id);
                const bags = items.reduce((s, it) => s + Number(it.quantity || 0), 0);
                const dt = o.delivery_date ? new Date(o.delivery_date + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
                return (
                  <Card key={o.id} className="p-3 border" style={{ borderColor: '#E2DFD8', background: 'white' }} data-testid="heatmap-drill-order-card">
                    <div className="flex items-center justify-between text-xs mb-2">
                      <div>
                        <div className="font-bold" style={{ color: '#2B5339' }}>Order #{o.id.slice(0, 8)}</div>
                        <div style={{ color: '#5C6B61' }}>Delivery: {dt} · Status: {o.status}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold tabular-nums" style={{ color: '#2B5339' }}>{bags.toLocaleString('en-IN')} bags</div>
                        {typeof o.total_amount === 'number' && (
                          <div className="text-[10px] tabular-nums" style={{ color: '#5C6B61' }}>{fmtINR(o.total_amount)}</div>
                        )}
                      </div>
                    </div>
                    <table className="w-full text-[11px] mb-2">
                      <tbody>
                        {items.map((it, i) => (
                          <tr key={i} className="border-t" style={{ borderColor: '#F0EDE6' }}>
                            <td className="py-1">{it.product_name}</td>
                            <td className="py-1 text-right tabular-nums" style={{ color: '#2B5339' }}>{Number(it.quantity || 0).toLocaleString('en-IN')} × {fmtINR(it.price)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {onEditOrder && (o.status === 'pending' || o.status === 'confirmed') && (
                      <div className="flex justify-start">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setDrillOrders(null); onEditOrder(o.id); }}
                          data-testid="heatmap-drill-edit-order-btn"
                          style={{ borderColor: '#2B5339', color: '#2B5339' }}
                        >
                          <Pencil className="w-3.5 h-3.5 mr-1" /> Edit Order
                        </Button>
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
